import * as Eta from 'eta';
import { CCM_FIELDS, fields } from '../ccm';
import { CustomerMapParsed, FieldMapping, SupportedTools } from '../interfaces';

Eta.configure({
  tags: ['{{!', '!}}'],
  useWith: true,
});

/**
 * Base definition for all Tool classes
 *
 * Allows overriding only the necessary methods per product as needed, without requiring consuming code
 * to have substantial branching for all minor variants between each platform
 */
export abstract class Tool {
  // Well-formatted name of the tool, used when displaying tool name to user
  public abstract readonly toolName: SupportedTools;

  // Default CCM values for all fields mapped to themselves, used
  // as lowest priority fallback fields to use when not provided by a customer fieldMapping
  private globalDefaultMappedFields: {
    [ccmField: string]: string;
  } = Object.keys(fields).reduce((obj, key) => {
    obj[key] = key;
    return obj;
  }, {});

  public readonly toolDefaultCCMFieldMappings: { [ccmField: string]: string } =
    {};

  public fieldMappingCustomFields: Array<CCM_FIELDS>;

  /**
   *
   * @param contentTemplate Content template string to be rendered using customer mappings
   * @param fieldMapping One set of matched mappings to be injected as template context
   */
  public renderContentTemplatefromFieldMapping(
    contentTemplate: string,
    fieldMapping?: FieldMapping,
  ): string {
    const finalFields = {};

    if (fieldMapping && fieldMapping.fields) {
      for (let i = 0; i < fieldMapping.fields.length; i++) {
        if (
          Object.values(fieldMapping.fields[i])[0] &&
          Object.values(fieldMapping.fields[i])[0] != ''
        )
          Object.assign(finalFields, fieldMapping.fields[i]);
      }
    }

    return Eta.render(
      contentTemplate,
      // Cascade priority of fields used from right-to-left
      Object.assign(
        {},
        this.globalDefaultMappedFields,
        this.toolDefaultCCMFieldMappings,
        finalFields,
      ),
    ) as string;
  }

  /**
   * Optional method to support uploading and parsing a custom mapping export from the respective platform
   *
   * @param fileContents File upload contents to parse into structure CustomerMapParsed
   */
  public parseMapUpload?(fileContents: string): CustomerMapParsed;

  /**
   *
   * @returns true when parseMapUpload method is defined on a Tool subclass
   */
  public supportsMapUploads = (): boolean => {
    return typeof this.parseMapUpload === 'function';
  };

  /**
   * Optional method to generate a direct-to-tool search permalink based on user's provided tool URLs
   * and the provided content query string
   *
   * @param rootURL Root URL to the tool
   * @param renderedContent The content to be serialized into the generated search URL based on the tool's serialization method
   */
  public generateSearchPermalink?(
    rootURL: string,
    renderedContent: string,
  ): string;

  /**
   *
   * @returns true when generateSearchPermalink method is defined on a Tool subclass
   */
  public supportsSearchPermalinks = (): boolean => {
    return typeof this.generateSearchPermalink === 'function';
  };
}

/**
 * Default Tool class used when no more specific class is available
 */
export class DefaultTool extends Tool {
  // TODO: Gross for workaround on abstract
  public readonly toolName = 'Default Tool' as SupportedTools;
}
